import React from 'react';

export interface TabHelpContent {
  title: string;
  description: string;
  /** ヘルプボタンのaria-label */
  ariaLabel: string;
  sections?: {
    title: string;
    items: string[];
  }[];
  shortcuts?: {
    key: string;
    action: string;
  }[];
}

export const tabHelpContent: Record<string, TabHelpContent> = {
  'data-table': {
    title: 'Data Table',
    description: 'Browse and interact with your dataset',
    ariaLabel: 'Data Table help - selection, filtering, and sorting',
    sections: [
      {
        title: 'Basic Operations',
        items: [
          'Click column header to select column',
          'Click row number to select row',
          'Click arrow in header to sort',
          'Use filter box to filter rows'
        ]
      },
      {
        title: 'Multi-Selection',
        items: [
          'Ctrl/Cmd + Click for multiple items',
          'Shift + Click for range selection',
          'Click "Clear" button to deselect all'
        ]
      },
      {
        title: 'Filter Expression Syntax',
        items: [
          "Comparison: age > 25, price <= 100, status != 'inactive'",
          "Pattern matching: name LIKE '%Smith%', email ILIKE '%@example%'",
          "Set operators: status IN ('active', 'pending'), age BETWEEN 20 AND 30",
          "Null check: column IS NULL, column IS NOT NULL",
          'Logical operators: age >= 18 and active = true',
          "Combine conditions: (age > 30 or salary > 50000) and department = 'IT'",
          "Values: numbers (42, 3.14), strings ('text'), booleans (true, false), null",
          'Column names with spaces: "First Name" = \'John\''
        ]
      },
      {
        title: 'Filter Operators',
        items: [
          'Comparison: = != > < >= <=',
          'Pattern: LIKE (% for any, _ for single char), ILIKE (case-insensitive)',
          'Set: IN (...), NOT IN (...), BETWEEN ... AND ..., NOT BETWEEN ... AND ...',
          'Null: IS NULL, IS NOT NULL',
          'Logical: and, or, NOT'
        ]
      }
    ],
    shortcuts: [
      { key: 'Ctrl+C', action: 'Copy selected data' },
      { key: 'Ctrl+A', action: 'Select all' }
    ]
  },

  'statistics': {
    title: 'Statistics',
    description: 'View statistical summaries of selected columns',
    ariaLabel: 'Statistics help - mean, median, standard deviation, and more',
    sections: [
      {
        title: 'How it works',
        items: [
          'Select columns in Data Table tab first',
          'Statistics calculate automatically',
          'Updates when selection changes'
        ]
      },
      {
        title: 'Available metrics',
        items: [
          'Count, Mean, Median, Mode',
          'Standard deviation, Variance',
          'Min, Max, Range',
          'Correlation matrix (for multiple columns)'
        ]
      }
    ]
  },

  'graph-builder': {
    title: 'Graph Builder',
    description: 'Create visualizations from your data',
    ariaLabel: 'Graph Builder help - create histogram, scatter plot, bar chart, and more',
    sections: [
      {
        title: 'Steps to create a graph',
        items: [
          '1. Choose graph type from dropdown',
          '2. Select columns for axes',
          '3. Click "Preview" to generate',
          '4. Optionally add to report'
        ]
      },
      {
        title: 'Available graph types',
        items: [
          'Histogram - Distribution of single variable',
          'Scatter Plot - Relationship between two variables',
          'Bar Chart - Compare categories',
          'Time Series - Data over time'
        ]
      }
    ]
  },

  'sql-editor': {
    title: 'SQL Editor',
    description: 'Query your data using DuckDB SQL',
    ariaLabel: 'SQL Editor help - SELECT, WHERE, GROUP BY query syntax',
    sections: [
      {
        title: 'Basic syntax',
        items: [
          'SELECT column1, column2 FROM dataset',
          'WHERE condition (e.g., age > 25)',
          'GROUP BY column',
          'ORDER BY column ASC/DESC'
        ]
      },
      {
        title: 'Editor features',
        items: [
          'Visual mode for building queries',
          'Syntax highlighting',
          'Query history',
          'Export results as new dataset'
        ]
      }
    ],
    shortcuts: [
      { key: 'Ctrl+Enter', action: 'Execute query' }
    ]
  },

  'report': {
    title: 'Report',
    description: 'Create analysis reports with text and visualizations',
    ariaLabel: 'Report help - add text, graphs, tables, and export',
    sections: [
      {
        title: 'Report elements',
        items: [
          'Text/Markdown blocks',
          'Graphs from Graph Builder',
          'Tables from data',
          'Statistical summaries'
        ]
      },
      {
        title: 'Operations',
        items: [
          'Drag to reorder elements',
          'Click to edit text',
          'Use menu (⋮) for element options',
          'Export as HTML for sharing'
        ]
      }
    ]
  },

  'crosstab': {
    title: 'Crosstab Analysis',
    description: 'Create pivot tables to summarize categorical data',
    ariaLabel: 'Crosstab help - pivot tables with row/column grouping and aggregation',
    sections: [
      {
        title: 'Setup',
        items: [
          'Choose row grouping variable',
          'Choose column grouping variable',
          'Select value to aggregate',
          'Pick aggregation function (sum, mean, count, etc.)'
        ]
      },
      {
        title: 'Features',
        items: [
          'Multiple aggregation functions',
          'Row and column totals',
          'Export as new dataset',
          'Percentage calculations'
        ]
      }
    ]
  }
};

// ヘルプコンテンツをReactコンポーネントとして表示
export const TabHelp: React.FC<{ tabType: string }> = ({ tabType }) => {
  const help = tabHelpContent[tabType];

  if (!help) {
    return (
      <div>
        <h4>Help</h4>
        <p>No help available for this tab type yet.</p>
      </div>
    );
  }

  return (
    <div>
      <h4>{help.title}</h4>
      <p style={{ marginBottom: '16px', fontSize: '13px', color: '#666' }}>
        {help.description}
      </p>

      {help.sections?.map((section, idx) => (
        <div key={idx} style={{ marginBottom: '16px' }}>
          <h5 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>
            {section.title}
          </h5>
          <ul style={{ margin: 0, paddingLeft: '20px' }}>
            {section.items.map((item, itemIdx) => (
              <li key={itemIdx} style={{ fontSize: '12px', marginBottom: '4px', color: '#444' }}>
                {item}
              </li>
            ))}
          </ul>
        </div>
      ))}

      {help.shortcuts && help.shortcuts.length > 0 && (
        <div style={{ marginTop: '16px' }}>
          <h5 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>
            Keyboard Shortcuts
          </h5>
          <ul style={{ margin: 0, paddingLeft: '20px' }}>
            {help.shortcuts.map((shortcut, idx) => (
              <li key={idx} style={{ fontSize: '12px', marginBottom: '4px' }}>
                <code style={{
                  background: '#f5f5f5',
                  padding: '2px 6px',
                  borderRadius: '3px',
                  fontSize: '11px'
                }}>
                  {shortcut.key}
                </code>
                <span style={{ marginLeft: '8px', color: '#444' }}>
                  {shortcut.action}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};